import React, { useEffect, useState } from "react";
import useRouter from "hooks/useRouter";
import useSearch from "redux/hooks/useSearch";
import { Action, Category, trackEvent } from "utils/trackEvent";
import {
  CarouselV2,
  VerticalDispensaryCard,
} from "@leafly-com/frontend-components";
import { Dispensary } from "custom-types/Dispensary";
import { BotanicIcon, Image } from "@leafly-com/frontend-components";

const DispensaryResults: React.FC = () => {
  const {
    parsedQuery: { q, searchCategory },
  } = useRouter();

  const { dispensary: dispensaries } = useSearch();

  const viewingCountOnLoad = searchCategory === "dispensary" ? 12 : 6;
  const [viewingCount, setViewingCount] = useState(viewingCountOnLoad);

  useEffect(() => {
    setViewingCount(viewingCountOnLoad);
  }, [searchCategory]);

  if (!dispensaries || !dispensaries.length) return null;

  return (
    <>
      {searchCategory === "dispensary" ? (
        // Display as grid when filtered to dispensaries
        <>
          <h3 className="heading--l">Dispensaries</h3>
          <span className="font-normal">matching "{q}"</span>
          <div className="row mt-md mb-lg">
            {dispensaries
              .slice(0, viewingCount)
              .map((dispensary: Dispensary) => {
                const {
                  city,
                  coverPhotoUrl,
                  deliveryEnabled,
                  distanceMi,
                  logoUrl,
                  name,
                  pickupEnabled,
                  preferredPickupPartner,
                  rating,
                  reviewCount,
                  schedule,
                  slug,
                  state,
                } = dispensary;
                return (
                  <div
                    className="col md:col-1/2 lg:col-1/3 mb-md"
                    key={dispensary.slug}
                  >
                    <div className="h-full rounded elevation-low overflow-hidden">
                      <VerticalDispensaryCard
                        city={city}
                        coverImage={logoUrl ? undefined : coverPhotoUrl}
                        deliveryEnabled={deliveryEnabled}
                        distance={distanceMi}
                        hours={schedule?.weekly}
                        logoImage={logoUrl}
                        name={name}
                        pickupEnabled={pickupEnabled}
                        preferredPickupPartner={preferredPickupPartner}
                        rating={rating}
                        reviewCount={reviewCount}
                        slug={slug}
                        state={state}
                        className="h-full w-full"
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </>
      ) : (
        <CarouselV2
          cardsPerSlide={3}
          title="Dispensaries"
          subtitle={<div className="font-normal mb-md">matching "{q}"</div>}
        >
          {dispensaries.slice(0, viewingCount).map((dispensary: Dispensary) => {
            const {
              city,
              coverPhotoUrl,
              deliveryEnabled,
              distanceMi,
              logoUrl,
              name,
              pickupEnabled,
              preferredPickupPartner,
              rating,
              reviewCount,
              schedule,
              slug,
              state,
            } = dispensary;
            return (
              <div
                className="h-full rounded elevation-low overflow-hidden bg-white"
                key={dispensary.slug}
              >
                <VerticalDispensaryCard
                  city={city}
                  coverImage={logoUrl ? undefined : coverPhotoUrl}
                  deliveryEnabled={deliveryEnabled}
                  distance={distanceMi}
                  gtmAction={Action.searchResultClicked}
                  gtmCategory={Category.searchResults}
                  hours={schedule?.weekly}
                  logoImage={logoUrl}
                  name={name}
                  pickupEnabled={pickupEnabled}
                  preferredPickupPartner={preferredPickupPartner}
                  rating={rating}
                  reviewCount={reviewCount}
                  slug={slug}
                  state={state}
                  className="h-full w-full"
                />
              </div>
            );
          })}
        </CarouselV2>
      )}
      <a
        className="button md:mb-xxl mt-lg"
        href={`/search/shop?q=${
          q ? encodeURIComponent(q) : ""
        }&searchCategory=dispensary `}
        onClick={() =>
          trackEvent(
            Category.searchResults,
            Action.searchResultClicked,
            "find nearby dispensaries button"
          )
        }
      >
        show all dispensary results
      </a>

      {
        // desktop link to finder CTA
      }
      <div className="relative text-center hidden md:flex mt-section border-t border-b border-light-grey">
        <Image
          className="hidden md:flex"
          sizes={[500, 1440]}
          src="https://leafly-public.imgix.net/global-search/seattle-map-lg.png"
          style={{ opacity: "0.5" }}
          alt="Example Dispensary Map"
        />
        <div className="finderCTA">
          <div className="text-md text-center ">
            Looking to find a nearby dispensary?
          </div>
          <a
            className="button flex items-center justify-center mt-xl"
            href="/dispensaries"
            onClick={() =>
              trackEvent(
                Category.searchResults,
                Action.searchResultClicked,
                "find nearby dispensaries button"
              )
            }
          >
            <BotanicIcon className="mx-sm" name="geolocation" /> view dispensary
            map
          </a>
        </div>
      </div>

      {
        //mobile link to finder CTA
      }
      <div className="relative border border-light-grey text-center md:hidden">
        <Image
          className="md:hidden"
          sizes={[320, 500]}
          src="https://leafly-public.imgix.net/global-search/seattle-map.png"
          style={{ opacity: "0.5" }}
          alt="Example Dispensary Map"
        />
        <div className="finderCTA">
          <div className="text-md text-center col font-semibold">
            Looking to find a nearby dispensary?
          </div>
          <a
            className="button mt-xl flex items-center justify-center"
            href="/dispensaries"
            onClick={() =>
              trackEvent(
                Category.searchResults,
                Action.searchResultClicked,
                "find nearby dispensaries button"
              )
            }
          >
            <BotanicIcon className="mx-sm" name="geolocation" />
            view dispensary map
          </a>
        </div>
      </div>
      <style jsx>{`
        .finderCTA {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-weight: 600;
        }

        @media screen and (max-width: 769px) {
          .finderCTA {
            padding: 0 1rem;
            width: 100%;
          }
        }
      `}</style>
    </>
  );
};

export default DispensaryResults;
